import {
  ArrowDownUp,
  Award,
  Banknote,
  Bell,
  Boxes,
  Building2,
  Calculator,
  ChartNoAxesColumn,
  CircleHelp,
  ClipboardCheck,
  Clock,
  Factory,
  Contact,
  Database,
  FileText,
  FolderTree,
  Gauge,
  Percent,
  Share2,
  MonitorSmartphone,
  House,
  MapPin,
  ShoppingBag,
  SlidersHorizontal,
  Tag,
  Truck,
  Users,
  Wallet,
  type LucideIcon,
  Store,
} from 'lucide-react';
import type { Permission } from '@lightbm/shared';

export type NavKey =
  | 'home'
  | 'stockInOut'
  | 'transfers'
  | 'counts'
  | 'suppliers'
  | 'supplierPayments'
  | 'customers'
  | 'orders'
  | 'payments'
  | 'documents'
  | 'promotions'
  | 'onlineStore'
  | 'seo'
  | 'pos'
  | 'registers'
  | 'stockLevels'
  | 'stockLimits'
  | 'products'
  | 'categories'
  | 'brands'
  | 'locations'
  | 'reports'
  | 'activity'
  | 'alerts'
  | 'company'
  | 'configuration'
  | 'users'
  | 'dataTools'
  | 'help';

export interface NavItem {
  key: NavKey;
  path: string;
  icon: LucideIcon;
  /** Shown when the user has at least one of them (none: everyone). */
  anyOf: Permission[];
}

export interface NavSection {
  key: 'main' | 'daily' | 'purchases' | 'sales' | 'stock' | 'followUp' | 'admin';
  items: NavItem[];
}

/** Navigation of the design ("Component navigation"), section by section. */
export const NAV_SECTIONS: NavSection[] = [
  { key: 'main', items: [{ key: 'home', path: '/', icon: House, anyOf: [] }] },
  {
    key: 'daily',
    items: [
      { key: 'stockInOut', path: '/operations', icon: ArrowDownUp, anyOf: ['stock:view'] },
      { key: 'transfers', path: '/transfers', icon: Truck, anyOf: ['transfers:view'] },
      { key: 'counts', path: '/counts', icon: ClipboardCheck, anyOf: ['counts:view'] },
    ],
  },
  // Suppliers and what is paid to them (docs/SUPPLIERS_PLAN.md).
  {
    key: 'purchases',
    items: [
      { key: 'suppliers', path: '/suppliers', icon: Factory, anyOf: ['suppliers:view'] },
      {
        key: 'supplierPayments',
        path: '/supplier-payments',
        icon: Banknote,
        anyOf: ['supplierPayments:view'],
      },
    ],
  },
  // Commerce (docs/COMMERCE_PLAN.md): the till, orders, payments, registers, customers.
  {
    key: 'sales',
    items: [
      { key: 'pos', path: '/pos', icon: MonitorSmartphone, anyOf: ['pos:sell'] },
      { key: 'orders', path: '/orders', icon: ShoppingBag, anyOf: ['orders:view'] },
      { key: 'payments', path: '/payments', icon: Wallet, anyOf: ['payments:view'] },
      {
        key: 'documents',
        path: '/documents',
        icon: FileText,
        anyOf: ['payments:view', 'orders:view'],
      },
      { key: 'promotions', path: '/promotions', icon: Percent, anyOf: ['promotions:view'] },
      {
        key: 'onlineStore',
        path: '/online-store',
        icon: Store,
        anyOf: ['products:edit', 'configuration:edit'],
      },
      // SEO-01: search engines and social media.
      { key: 'seo', path: '/online-store/seo', icon: Share2, anyOf: ['configuration:view'] },
      {
        key: 'registers',
        path: '/registers',
        icon: Calculator,
        anyOf: ['registers:view', 'registers:manage'],
      },
      { key: 'customers', path: '/customers', icon: Contact, anyOf: ['customers:view'] },
    ],
  },
  {
    key: 'stock',
    items: [
      { key: 'stockLevels', path: '/stock', icon: Boxes, anyOf: ['stock:view'] },
      { key: 'stockLimits', path: '/stock/limits', icon: Gauge, anyOf: ['stock:view'] },
      { key: 'products', path: '/products', icon: Tag, anyOf: ['products:view'] },
      { key: 'categories', path: '/categories', icon: FolderTree, anyOf: ['products:view'] },
      { key: 'brands', path: '/brands', icon: Award, anyOf: ['products:view'] },
      { key: 'locations', path: '/locations', icon: MapPin, anyOf: ['locations:view'] },
    ],
  },
  {
    key: 'followUp',
    items: [
      { key: 'reports', path: '/reports', icon: ChartNoAxesColumn, anyOf: ['analytics:view'] },
      {
        key: 'activity',
        path: '/activity',
        icon: Clock,
        anyOf: ['activity:view', 'activity:viewMovements'],
      },
      { key: 'alerts', path: '/alerts', icon: Bell, anyOf: ['alerts:view'] },
    ],
  },
  {
    key: 'admin',
    items: [
      { key: 'company', path: '/company', icon: Building2, anyOf: ['company:view'] },
      {
        key: 'configuration',
        path: '/configuration',
        icon: SlidersHorizontal,
        anyOf: ['configuration:view'],
      },
      { key: 'users', path: '/users', icon: Users, anyOf: ['users:view'] },
      {
        key: 'dataTools',
        path: '/data-tools',
        icon: Database,
        anyOf: ['dataTools:import', 'dataTools:export', 'dataTools:backup'],
      },
    ],
  },
];

export const HELP_ITEM: NavItem = {
  key: 'help',
  path: '/help',
  icon: CircleHelp,
  anyOf: [],
};

export function visibleSections(canAny: (...permissions: Permission[]) => boolean): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => item.anyOf.length === 0 || canAny(...item.anyOf)),
  })).filter((section) => section.items.length > 0);
}
